import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip, IconButton, Menu, MenuItem, Tooltip, FormControl, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, Popover, Button, InputAdornment, Divider, useMediaQuery, useTheme, Drawer, Skeleton, LinearProgress } from '@mui/material';
import { Icon } from '@iconify/react';
export interface Column<T> {
  id: string;
  label: string;
  renderCell?: (row: T) => React.ReactNode;
  renderFilter?: () => React.ReactNode;
  filterValue?: any;
  onFilterChange?: (value: any) => void;
  sortable?: boolean;
}
export interface RowAction<T> {
  icon: string;
  label: string;
  onClick: (row: T) => void;
  condition?: (row: T) => boolean;
}
export interface FilterChip {
  id: string;
  label: string;
  value: string | number | boolean;
  onDelete: () => void;
}
export interface DataTableProps<T> {
  title: string;
  data: T[];
  columns: Column<T>[];
  rowActions?: RowAction<T>[];
  keyField: keyof T;
  filterChips?: FilterChip[];
  onClearAllFilters?: () => void;
  loading?: boolean;
  initialVisibleColumns?: string[];
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchValue?: string;
  onExport?: () => void;
  emptyStateMessage?: string;
  rowsPerPageOptions?: number[];
  showColumnFilters?: boolean;
  onRowClick?: (row: T) => void;
  showSearch?: boolean;
}
function DataTable<T>({
  title,
  data,
  columns,
  rowActions,
  keyField,
  filterChips,
  onClearAllFilters,
  loading = false,
  initialVisibleColumns,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onExport,
  emptyStateMessage = 'No data found',
  rowsPerPageOptions = [10, 25, 50, 100],
  showColumnFilters = true,
  onRowClick,
  showSearch = false
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);
  // FIXED: Always include 'actions' in visible columns if rowActions exist
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const cols = initialVisibleColumns || columns.map(col => col.id);
    // Add 'actions' if rowActions exist and it's not already in the list
    if (rowActions && !cols.includes('actions')) {
      return [...cols, 'actions'];
    }
    return cols;
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [density, setDensity] = useState<'medium' | 'compact'>('medium');
  const [search, setSearch] = useState(searchValue);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: T) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };
  const handleColumnsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnsMenuAnchor(event.currentTarget);
  };
  const handleColumnsMenuClose = () => {
    setColumnsMenuAnchor(null);
  };
  const handleColumnToggle = (columnId: string) => {
    if (visibleColumns.includes(columnId)) {
      if (visibleColumns.length > 1) {
        setVisibleColumns(visibleColumns.filter(col => col !== columnId));
      }
    } else {
      setVisibleColumns([...visibleColumns, columnId]);
    }
  };
  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      setFilterDrawerOpen(true);
    } else {
      setFilterMenuAnchor(event.currentTarget);
    }
  };
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
    setFilterDrawerOpen(false);
  };
  const handleDensityChange = () => {
    setDensity(density === 'medium' ? 'compact' : 'medium');
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };
  const clearSearch = () => {
    setSearch('');
    if (onSearchChange) {
      onSearchChange('');
    }
  };
  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  if (loading) {
    return <Card elevation={0} sx={{
      borderRadius: 2,
      border: '1px solid #E5E7EB'
    }} data-id="element-767">
        <CardContent sx={{
        p: 0,
        position: 'relative'
      }} data-id="element-768">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          flexWrap: 'wrap',
          gap: 1
        }} data-id="element-769">
            <Skeleton variant="text" width={150} height={32} data-id="element-770" />
            <Box sx={{
            display: 'flex',
            gap: 1
          }} data-id="element-771">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="circular" width={32} height={32} data-id="element-772" />)}
            </Box>
          </Box>
          <Divider data-id="element-773" />
          <LinearProgress sx={{
          height: 3,
          background: 'rgba(0, 82, 204, 0.1)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#0052CC'
          }
        }} data-id="element-774" />
          <TableContainer data-id="element-775">
            <Table size={density} stickyHeader data-id="element-776">
              <TableHead data-id="element-777">
                <TableRow data-id="element-778">
                  {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} sx={{
                  backgroundColor: '#F3F5F7',
                  color: '#0052CC',
                  fontWeight: 700
                }} data-id="element-779">
                          <Skeleton variant="text" width="80%" height={24} data-id="element-780" />
                        </TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody data-id="element-781">
                {[...Array(5)].map((_, index) => <TableRow key={index} data-id="element-782">
                    {visibleColumns.map(column => <TableCell key={column} data-id="element-783">
                        <Skeleton variant="text" width="90%" height={24} data-id="element-784" />
                      </TableCell>)}
                  </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider data-id="element-785" />
          <Box p={2} data-id="element-786">
            <Skeleton variant="rectangular" width="100%" height={52} data-id="element-787" />
          </Box>
        </CardContent>
      </Card>;
  }
  return <Card elevation={0} sx={{
    borderRadius: 2,
    border: '1px solid #E5E7EB',
    maxWidth: '100%',
    overflow: 'hidden'
  }} data-id="element-788">
      <CardContent sx={{
      p: 0,
      position: 'relative',
      maxWidth: '100%'
    }} data-id="element-789">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        flexWrap: 'wrap',
        gap: 1
      }} data-id="element-790">
          <Typography variant="subtitle1" sx={{
          fontWeight: 500
        }} data-id="element-791">
            {title} ({data.length})
          </Typography>
          <Box sx={{
          display: 'flex',
          gap: 1
        }} data-id="element-792">
            <Tooltip title="Columns" data-id="element-793">
              <IconButton size="small" onClick={handleColumnsMenuOpen} data-id="element-794">
                <Icon icon="lucide:columns" width={18} height={18} data-id="element-795" />
              </IconButton>
            </Tooltip>
            {onExport && <Tooltip title="Export" data-id="element-796">
                <IconButton size="small" onClick={onExport} data-id="element-797">
                  <Icon icon="lucide:download" width={18} height={18} data-id="element-798" />
                </IconButton>
              </Tooltip>}
          </Box>
        </Box>
        {/* Filter chips */}
        {filterChips && filterChips.length > 0 && <Box sx={{
        px: 2,
        pb: 2,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1
      }} data-id="element-799">
            {filterChips.map(chip => <Chip key={chip.id} label={`${chip.label}: ${chip.value}`} onDelete={chip.onDelete} size="small" data-id="element-800" />)}
            {onClearAllFilters && <Chip label="Clear all" onClick={onClearAllFilters} size="small" variant="outlined" data-id="element-801" />}
          </Box>}
        <Divider data-id="element-802" />
        <TableContainer sx={{
        overflowX: 'auto',
        maxWidth: '100%',
        '&::-webkit-scrollbar': {
          height: 8
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#F3F4F6'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#D1D5DB',
          borderRadius: 4,
          '&:hover': {
            backgroundColor: '#9CA3AF'
          }
        }
      }} data-id="element-803">
          <Table size="medium" stickyHeader data-id="element-804">
            <TableHead data-id="element-805">
              {showColumnFilters && <TableRow data-id="element-806">
                  {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={`filter-${column.id}`} sx={{
                backgroundColor: '#F3F5F7',
                padding: '8px 16px',
                borderBottom: 'none',
                verticalAlign: 'top'
              }} data-id="element-807">
                          {column.renderFilter && column.renderFilter()}
                        </TableCell>)}
                  {rowActions && visibleColumns.includes('actions') && <TableCell sx={{
                backgroundColor: '#F3F5F7',
                padding: '8px 16px',
                borderBottom: 'none'
              }} data-id="element-808" />}
                </TableRow>}
              <TableRow data-id="element-809">
                {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} sx={{
                backgroundColor: '#F3F5F7',
                color: '#0052CC',
                fontWeight: 700,
                transition: 'none'
              }} data-id="element-810">
                        {column.label}
                      </TableCell>)}
                {rowActions && visibleColumns.includes('actions') && <TableCell sx={{
                backgroundColor: '#F3F5F7',
                color: '#0052CC',
                fontWeight: 700,
                transition: 'none'
              }} data-id="element-811">
                    Actions
                  </TableCell>}
              </TableRow>
            </TableHead>
            <TableBody data-id="element-812">
              {paginatedData.map(row => <TableRow key={String(row[keyField])} hover onClick={onRowClick ? () => onRowClick(row) : undefined} sx={{
              cursor: onRowClick ? 'pointer' : 'default',
              '&:hover': onRowClick ? {
                backgroundColor: '#F9FAFB'
              } : undefined
            }} data-id="element-813">
                  {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} data-id="element-814">
                          {column.renderCell ? column.renderCell(row) : String(row[column.id as keyof T] || '')}
                        </TableCell>)}
                  {rowActions && visibleColumns.includes('actions') && <TableCell data-id="element-815">
                      <Box sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }} data-id="element-816">
                        <Tooltip title="Actions" data-id="element-817">
                          <IconButton size="small" onClick={event => {
                      event.stopPropagation();
                      handleMenuClick(event, row);
                    }} sx={{
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: '#F3F5F7'
                      }
                    }} data-id="element-818">
                            <Icon icon="lucide:more-vertical" width={18} height={18} data-id="element-819" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>}
                </TableRow>)}
              {paginatedData.length === 0 && <TableRow data-id="element-820">
                  <TableCell colSpan={visibleColumns.length + (rowActions && visibleColumns.includes('actions') ? 1 : 0)} align="center" sx={{
                py: 3
              }} data-id="element-821">
                    {emptyStateMessage}
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Row Actions Menu */}
        {rowActions && <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }} transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }} PaperProps={{
        sx: {
          minWidth: 180,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          borderRadius: 2
        }
      }} data-id="element-822">
            {selectedRow && rowActions.filter(action => !action.condition || action.condition(selectedRow)).map((action, index) => <MenuItem key={index} onClick={event => {
          event.stopPropagation();
          action.onClick(selectedRow);
          handleMenuClose();
        }} sx={{
          fontSize: '0.85rem',
          gap: 1.5,
          py: 1,
          color: action.label === 'Cancel' || action.label === 'Cancel Transaction' || action.label === 'Reject' ? '#AE2A19' : action.label === 'Approve' ? '#0B7923' : action.label === 'View Details' || action.label === 'Amend' ? '#0052CC' : '#374151'
        }} data-id="element-823">
                    <Icon icon={action.icon} width={16} height={16} color={action.label === 'View Details' || action.label === 'Amend' ? '#0052CC' : action.label === 'Cancel' || action.label === 'Cancel Transaction' || action.label === 'Reject' ? '#AE2A19' : action.label === 'Approve' ? '#0B7923' : '#6B7280'} data-id="element-824" />
                    {action.label}
                  </MenuItem>)}
          </Menu>}
        <TablePagination rowsPerPageOptions={rowsPerPageOptions} component="div" count={data.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} data-id="element-825" />
      </CardContent>
      {/* Column visibility popover */}
      <Popover open={Boolean(columnsMenuAnchor)} anchorEl={columnsMenuAnchor} onClose={handleColumnsMenuClose} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} data-id="element-826">
        <Box sx={{
        p: 2,
        width: 200
      }} data-id="element-827">
          <Typography variant="subtitle2" sx={{
          mb: 1
        }} data-id="element-828">
            Show Columns
          </Typography>
          {columns.map(column => <Box key={column.id} sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 0.5
        }} data-id="element-829">
              <Checkbox checked={visibleColumns.includes(column.id)} onChange={() => handleColumnToggle(column.id)} size="small" disabled={visibleColumns.length === 1 && visibleColumns.includes(column.id)} data-id="element-830" />
              <Typography variant="body2" data-id="element-831">{column.label}</Typography>
            </Box>)}
          {rowActions && <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 0.5
        }} data-id="element-832">
              <Checkbox checked={visibleColumns.includes('actions')} onChange={() => handleColumnToggle('actions')} size="small" disabled={visibleColumns.length === 1 && visibleColumns.includes('actions')} data-id="element-833" />
              <Typography variant="body2" data-id="element-834">Actions</Typography>
            </Box>}
        </Box>
      </Popover>
    </Card>;
}
export default DataTable;